import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Globe, MouseSimple, CreditCard, Ticket, Coins, UsersThree, Warning,
  Envelope, DownloadSimple, MapTrifold, Pulse, SealCheck, ArrowsLeftRight, Rocket,
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [surfWindow, setSurfWindow] = useState("today"); // 'today' | 'yesterday'

  useEffect(() => {
    const load = () => api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const copyAllEmails = async () => {
    try {
      const { data: d } = await api.get(`/admin/users/emails`);
      await navigator.clipboard.writeText(d.csv || "");
      toast.success(`Copied ${d.count} email${d.count === 1 ? "" : "s"} to clipboard`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const downloadEmailsCsv = () => {
    const url = `${api.defaults.baseURL}/admin/users/emails?format=csv`;
    window.open(url, "_blank");
  };

  const headline = [
    { key: "surfs_total", label: "Total sites surfed (all time)", icon: MouseSimple, color: "from-purple-600 to-purple-900", testid: "kpi-surfs-total" },
    { key: "users_total", label: "Total users", icon: Users, color: "from-blue-600 to-blue-900", testid: "kpi-users-total" },
    // 3rd headline slot is rendered manually below so we can attach a Today/Yesterday toggle.
  ];

  const cards = [
    { key: "users_active_24h", label: "Active (24h)", icon: Users, color: "text-green-500", testid: "kpi-users-active" },
    { key: "new_users_7d", label: "New (7d)", icon: Users, color: "text-yellow-500", testid: "kpi-users-new" },
    { key: "websites_total", label: "Websites", icon: Globe, color: "text-indigo-500", testid: "kpi-websites" },
    { key: "websites_pending", label: "Pending sites", icon: Warning, color: "text-orange-500", testid: "kpi-websites-pending" },
    { key: "pending_payments", label: "Pending payments", icon: CreditCard, color: "text-red-500", testid: "kpi-payments" },
    { key: "open_tickets", label: "Open tickets", icon: Ticket, color: "text-pink-500", testid: "kpi-tickets" },
    { key: "credits_earned_total", label: "Credits earned", icon: Coins, color: "text-yellow-500", testid: "kpi-credits-earned" },
    { key: "credits_spent_total", label: "Credits spent", icon: Coins, color: "text-orange-500", testid: "kpi-credits-spent" },
    { key: "referrals_total", label: "Referrals", icon: UsersThree, color: "text-cyan-500", testid: "kpi-referrals" },
    { key: "splash_pages_total", label: "Splash pages", icon: Rocket, color: "text-lime-500", testid: "kpi-splash-pages" },
  ];

  const fmt = (v) => (typeof v === "number" ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }) : (v ?? "—"));

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Overview</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="dashboard-copy-emails-btn"
            onClick={copyAllEmails}
            className="inline-flex items-center gap-1 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2 transition-colors duration-200"
            title="Copy every registered email as a comma-separated list — ready to paste into Gmail"
          >
            <Envelope size={14} weight="bold" /> Copy emails
          </button>
          <button
            data-testid="dashboard-download-emails-csv"
            onClick={downloadEmailsCsv}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-bold px-4 py-2 transition-colors duration-200"
            title="Download emails as CSV"
          >
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        {headline.map((c) => (
          <div key={c.key} data-testid={c.testid} className={`rounded-2xl bg-gradient-to-br ${c.color} text-white p-5 shadow-sm`}>
            <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-widest">
              <c.icon size={16} weight="fill" /> {c.label}
            </div>
            <div className="text-4xl sm:text-5xl font-black tracking-tighter mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              {fmt(data?.[c.key])}
            </div>
          </div>
        ))}

        {/* Surfs today / yesterday — toggleable */}
        <div
          data-testid={surfWindow === "today" ? "kpi-surfs-today" : "kpi-surfs-yesterday"}
          className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-900 text-white p-5 shadow-sm relative"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase tracking-widest">
              <Pulse size={16} weight="fill" />
              {surfWindow === "today" ? "Surfs today" : "Surfs yesterday"}
            </div>
            <button
              data-testid="kpi-surfs-toggle"
              onClick={() => setSurfWindow((w) => (w === "today" ? "yesterday" : "today"))}
              title={surfWindow === "today" ? "Show yesterday's surfs (24-48 h ago)" : "Show today's surfs (last 24 h)"}
              className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 transition-colors duration-200"
            >
              <ArrowsLeftRight size={11} weight="bold" />
              {surfWindow === "today" ? "Yesterday" : "Today"}
            </button>
          </div>
          <div
            className="text-4xl sm:text-5xl font-black tracking-tighter mt-2"
            style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            data-testid={surfWindow === "today" ? "kpi-surfs-today-value" : "kpi-surfs-yesterday-value"}
          >
            {fmt(data?.[surfWindow === "today" ? "surfs_today" : "surfs_yesterday"])}
          </div>
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.key} data-testid={c.testid} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <c.icon size={22} weight="fill" className={c.color} />
            <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              {fmt(data?.[c.key])}
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Quick links */}
      <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/admin/users" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:shadow-md transition-shadow duration-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 flex items-center justify-center"><Users size={20} weight="bold" /></div>
          <div>
            <div className="font-black text-slate-900 dark:text-white">Manage users</div>
            <div className="text-xs text-slate-500">Search, paginate, verify</div>
          </div>
        </Link>
        <Link to="/admin/verification" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:shadow-md transition-shadow duration-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 flex items-center justify-center"><SealCheck size={20} weight="fill" /></div>
          <div>
            <div className="font-black text-slate-900 dark:text-white">Verification queue</div>
            <div className="text-xs text-slate-500">Review blue-check requests</div>
          </div>
        </Link>
        <Link to="/admin/visitors" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:shadow-md transition-shadow duration-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 flex items-center justify-center"><MapTrifold size={20} weight="bold" /></div>
          <div>
            <div className="font-black text-slate-900 dark:text-white">Visitor analytics</div>
            <div className="text-xs text-slate-500">See signup traffic sources</div>
          </div>
        </Link>
        <Link to="/admin/activity" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:shadow-md transition-shadow duration-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 flex items-center justify-center"><Pulse size={20} weight="bold" /></div>
          <div>
            <div className="font-black text-slate-900 dark:text-white">Live activity</div>
            <div className="text-xs text-slate-500">Who&apos;s online and surfing now</div>
          </div>
        </Link>
      </div>
    </AdminLayout>
  );
}
